/**
 * ESG SaaS Platform - Social API Service
 *
 * API service for social data (GRI 400 series).
 */

import { api } from './api';

// Types
export type SocialCategory = 'Employment' | 'Health & Safety' | 'Diversity & Inclusion' | 'Training & Development' | 'Human Rights';

// Employment
export interface EmploymentEntryCreate {
  facility_id: string;
  reporting_period: string;
  total_employees: number;
  by_employment_type: { full_time: number; part_time: number; temporary: number; contractors: number };
  by_gender: { male: number; female: number; other: number };
  by_age_group: { under_30: number; between_30_50: number; over_50: number };
  new_hires: number;
  turnover: number;
  turnover_rate: number;
}

export interface EmploymentEntry extends EmploymentEntryCreate {
  id: string;
  facility_name: string;
  status: string;
  created_by: string;
  created_at: string;
  approved_by?: string;
  approved_at?: string;
}

// Health & Safety
export interface HealthSafetyEntryCreate {
  facility_id: string;
  reporting_period: string;
  total_hours_worked: number;
  recordable_incidents: number;
  lost_time_incidents: number;
  lost_days: number;
  fatalities: number;
  near_misses: number;
  safety_training_hours: number;
}

export interface HealthSafetyEntry extends HealthSafetyEntryCreate {
  id: string;
  facility_name: string;
  trir: number;
  ltir: number;
  status: string;
  created_by: string;
  created_at: string;
  approved_by?: string;
  approved_at?: string;
}

// Diversity
export interface DiversityEntryCreate {
  facility_id: string;
  reporting_period: string;
  gender_breakdown: Record<string, { Male: number; Female: number; Other: number }>;
  age_breakdown: Record<string, number>;
  minority_representation: number;
  disability_representation: number;
  veteran_representation?: number;
  pay_equity_ratio: number;
}

export interface DiversityEntry extends DiversityEntryCreate {
  id: string;
  facility_name: string;
  female_percentage: number;
  status: string;
  created_by: string;
  created_at: string;
  approved_by?: string;
  approved_at?: string;
}

// Training
export interface TrainingEntryCreate {
  facility_id: string;
  reporting_period: string;
  total_training_hours: number;
  employees_trained: number;
  by_training_type: Record<string, number>;
  by_gender: { male: number; female: number };
  by_employee_level: Record<string, number>;
  average_hours_per_employee: number;
  training_investment: number;
}

export interface TrainingEntry extends TrainingEntryCreate {
  id: string;
  facility_name: string;
  status: string;
  created_by: string;
  created_at: string;
  approved_by?: string;
  approved_at?: string;
}

// Human Rights
export interface HumanRightsEntryCreate {
  facility_id: string;
  reporting_period: string;
  suppliers_assessed: number;
  suppliers_with_issues: number;
  human_rights_training_hours: number;
  grievances_received: number;
  grievances_resolved: number;
  child_labor_incidents: number;
  forced_labor_incidents: number;
  discrimination_incidents: number;
}

export interface HumanRightsEntry extends HumanRightsEntryCreate {
  id: string;
  facility_name: string;
  supplier_compliance_rate: number;
  grievance_resolution_rate: number;
  status: string;
  created_by: string;
  created_at: string;
  approved_by?: string;
  approved_at?: string;
}

// Summary
export interface SocialSummary {
  total_employees: number;
  female_percentage: number;
  turnover_rate: number;
  trir: number;
  ltir: number;
  training_hours_per_employee: number;
  diversity_index: number;
  supplier_compliance_rate: number;
  by_category: Record<string, any>;
  approved_count: number;
  pending_count: number;
  completion_percentage: number;
}

// Query parameters
interface ListParams {
  period?: string;
  facility?: string;
  status?: string;
  limit?: number;
  offset?: number;
}

// Service functions
export const socialService = {
  // Employment
  employment: {
    create: (entry: EmploymentEntryCreate) =>
      api.post<EmploymentEntry>('/social/employment', entry),

    list: (params: ListParams = {}) => {
      const query = new URLSearchParams();
      if (params.period) query.append('period', params.period);
      if (params.facility) query.append('facility', params.facility);
      if (params.status) query.append('status', params.status);
      if (params.limit) query.append('limit', params.limit.toString());
      if (params.offset) query.append('offset', params.offset.toString());
      const queryString = query.toString();
      return api.get<{ entries: EmploymentEntry[]; total: number; total_employees: number; average_turnover: number }>(
        `/social/employment${queryString ? `?${queryString}` : ''}`
      );
    },

    get: (entryId: string) =>
      api.get<EmploymentEntry>(`/social/employment/${entryId}`),

    update: (entryId: string, entry: EmploymentEntryCreate) =>
      api.put<{ id: string; updated: boolean }>(`/social/employment/${entryId}`, entry),

    delete: (entryId: string) =>
      api.delete<{ id: string; deleted: boolean }>(`/social/employment/${entryId}`),
  },

  // Health & Safety
  healthSafety: {
    create: (entry: HealthSafetyEntryCreate) =>
      api.post<HealthSafetyEntry>('/social/health-safety', entry),

    list: (params: ListParams = {}) => {
      const query = new URLSearchParams();
      if (params.period) query.append('period', params.period);
      if (params.facility) query.append('facility', params.facility);
      if (params.status) query.append('status', params.status);
      if (params.limit) query.append('limit', params.limit.toString());
      if (params.offset) query.append('offset', params.offset.toString());
      const queryString = query.toString();
      return api.get<{ entries: HealthSafetyEntry[]; total: number; overall_trir: number; overall_ltir: number }>(
        `/social/health-safety${queryString ? `?${queryString}` : ''}`
      );
    },

    get: (entryId: string) =>
      api.get<HealthSafetyEntry>(`/social/health-safety/${entryId}`),

    update: (entryId: string, entry: HealthSafetyEntryCreate) =>
      api.put<{ id: string; updated: boolean }>(`/social/health-safety/${entryId}`, entry),

    delete: (entryId: string) =>
      api.delete<{ id: string; deleted: boolean }>(`/social/health-safety/${entryId}`),
  },

  // Diversity
  diversity: {
    create: (entry: DiversityEntryCreate) =>
      api.post<DiversityEntry>('/social/diversity', entry),

    list: (params: ListParams = {}) => {
      const query = new URLSearchParams();
      if (params.period) query.append('period', params.period);
      if (params.facility) query.append('facility', params.facility);
      if (params.status) query.append('status', params.status);
      if (params.limit) query.append('limit', params.limit.toString());
      if (params.offset) query.append('offset', params.offset.toString());
      const queryString = query.toString();
      return api.get<{ entries: DiversityEntry[]; total: number; average_female_percentage: number; average_pay_equity: number }>(
        `/social/diversity${queryString ? `?${queryString}` : ''}`
      );
    },

    get: (entryId: string) =>
      api.get<DiversityEntry>(`/social/diversity/${entryId}`),

    update: (entryId: string, entry: DiversityEntryCreate) =>
      api.put<{ id: string; updated: boolean }>(`/social/diversity/${entryId}`, entry),

    delete: (entryId: string) =>
      api.delete<{ id: string; deleted: boolean }>(`/social/diversity/${entryId}`),
  },

  // Training
  training: {
    create: (entry: TrainingEntryCreate) =>
      api.post<TrainingEntry>('/social/training', entry),

    list: (params: ListParams = {}) => {
      const query = new URLSearchParams();
      if (params.period) query.append('period', params.period);
      if (params.facility) query.append('facility', params.facility);
      if (params.status) query.append('status', params.status);
      if (params.limit) query.append('limit', params.limit.toString());
      if (params.offset) query.append('offset', params.offset.toString());
      const queryString = query.toString();
      return api.get<{ entries: TrainingEntry[]; total: number; total_hours: number; total_investment: number }>(
        `/social/training${queryString ? `?${queryString}` : ''}`
      );
    },

    get: (entryId: string) =>
      api.get<TrainingEntry>(`/social/training/${entryId}`),

    update: (entryId: string, entry: TrainingEntryCreate) =>
      api.put<{ id: string; updated: boolean }>(`/social/training/${entryId}`, entry),

    delete: (entryId: string) =>
      api.delete<{ id: string; deleted: boolean }>(`/social/training/${entryId}`),
  },

  // Human Rights
  humanRights: {
    create: (entry: HumanRightsEntryCreate) =>
      api.post<HumanRightsEntry>('/social/human-rights', entry),

    list: (params: ListParams = {}) => {
      const query = new URLSearchParams();
      if (params.period) query.append('period', params.period);
      if (params.facility) query.append('facility', params.facility);
      if (params.status) query.append('status', params.status);
      if (params.limit) query.append('limit', params.limit.toString());
      if (params.offset) query.append('offset', params.offset.toString());
      const queryString = query.toString();
      return api.get<{ entries: HumanRightsEntry[]; total: number; overall_compliance_rate: number }>(
        `/social/human-rights${queryString ? `?${queryString}` : ''}`
      );
    },

    get: (entryId: string) =>
      api.get<HumanRightsEntry>(`/social/human-rights/${entryId}`),

    update: (entryId: string, entry: HumanRightsEntryCreate) =>
      api.put<{ id: string; updated: boolean }>(`/social/human-rights/${entryId}`, entry),

    delete: (entryId: string) =>
      api.delete<{ id: string; deleted: boolean }>(`/social/human-rights/${entryId}`),
  },

  // Summary
  getSummary: (period?: string) => {
    const query = period ? `?period=${period}` : '';
    return api.get<SocialSummary>(`/social/summary${query}`);
  },

  // Trends
  getTrends: (category: SocialCategory, startPeriod: string, endPeriod: string) =>
    api.get<{ category: string; periods: string[]; metrics: Record<string, any> }>(
      `/social/trends?category=${encodeURIComponent(category)}&start_period=${startPeriod}&end_period=${endPeriod}`
    ),

  // Bulk upload
  bulkUpload: async (file: File, category: SocialCategory, period: string) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.postFormData(
      `/social/bulk-upload?category=${encodeURIComponent(category)}&period=${encodeURIComponent(period)}`,
      formData
    );
  },

  // Export
  exportData: (format: string = 'csv', category?: SocialCategory, period?: string) => {
    const query = new URLSearchParams({ format });
    if (category) query.append('category', category);
    if (period) query.append('period', period);
    return api.get<{ status: string; format: string; download_url: string }>(`/social/export?${query.toString()}`);
  },
};
